import type { MsPillar, Severity, ResolutionTier, Relationship } from '../api/client';

/**
 * The six Microsoft Zero Trust pillars drive navigation, because customers
 * recognise Microsoft's language in a Microsoft assessment.
 *
 * CISA's five pillars are held separately on each primitive and drive maturity
 * reporting. The two models do not align one to one: Microsoft separates
 * infrastructure from network, CISA folds much of infrastructure into
 * applications and workloads. Deriving one from the other would lose that.
 */
export interface PillarDefinition {
  id: MsPillar;
  label: string;
  description: string;
  route: string;
  /** Identity colour. Gives each pillar a consistent presence across every
   *  chart, tile and badge, so a reader learns the colour once. */
  accent: string;
}

export const PILLARS: readonly PillarDefinition[] = [
  {
    id: 'identity',
    label: 'Identity',
    description: 'Accounts, authentication, privileged access and governance.',
    route: '/pillars/identity',
    accent: '#8764B8',
  },
  {
    id: 'endpoints',
    label: 'Endpoints',
    description: 'Device compliance, malware defence and vulnerability exposure.',
    route: '/pillars/endpoints',
    accent: '#038387',
  },
  {
    id: 'data',
    label: 'Data',
    description: 'Classification, loss prevention, retention and external sharing.',
    route: '/pillars/data',
    accent: '#0F6CBD',
  },
  {
    id: 'apps',
    label: 'Apps',
    description: 'Application consent, inventory and credential hygiene.',
    route: '/pillars/apps',
    accent: '#C239B3',
  },
  {
    id: 'infrastructure',
    label: 'Infrastructure',
    description: 'Secure configuration, encryption at rest, secrets and logging.',
    route: '/pillars/infrastructure',
    accent: '#CA5010',
  },
  {
    id: 'network',
    label: 'Network',
    description: 'Mail flow protection and private connectivity.',
    route: '/pillars/network',
    accent: '#107C10',
  },
] as const;

/** Pillar accent by id, for charts and badges. */
export function pillarAccent(id: MsPillar): string {
  return PILLARS.find((p) => p.id === id)?.accent ?? '#616161';
}

/**
 * A translucent wash of the pillar colour.
 *
 * Theme aware by construction: an alpha colour composites over whatever surface
 * sits beneath it, so the same value reads correctly on a light and a dark
 * background. The previous fixed light tint painted a near white panel in the
 * dark theme.
 */
export function pillarWash(id: MsPillar, alpha = 0.08): string {
  const hex = pillarAccent(id).replace('#', '');
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export const SEVERITY_ORDER: readonly Severity[] = [
  'Critical',
  'High',
  'Medium',
  'Low',
  'Info',
] as const;

/** Fluent status tokens rather than invented colours. */
export function severityTokens(severity: Severity): {
  foreground: string;
  background: string;
} {
  switch (severity) {
    case 'Critical':
      return {
        foreground: 'var(--colorStatusDangerForeground1)',
        background: 'var(--colorStatusDangerBackground1)',
      };
    case 'High':
      return {
        foreground: 'var(--colorPaletteDarkOrangeForeground1)',
        background: 'var(--colorPaletteDarkOrangeBackground1)',
      };
    case 'Medium':
      return {
        foreground: 'var(--colorStatusWarningForeground1)',
        background: 'var(--colorStatusWarningBackground1)',
      };
    case 'Low':
      return {
        foreground: 'var(--colorBrandForeground1)',
        background: 'var(--colorBrandBackground2)',
      };
    default:
      return {
        foreground: 'var(--colorNeutralForeground3)',
        background: 'var(--colorNeutralBackground3)',
      };
  }
}

/**
 * How a framework mapping was reached. A control found through the global
 * fallback is a weaker statement than one the customer asked to be measured
 * against, and the interface should not present them identically.
 */
export function tierDescription(tier: ResolutionTier): string {
  switch (tier) {
    case 'selected':
      return 'Matched a framework this customer selected.';
    case 'fallback':
      return 'No selected framework matched, so the global fallback answered.';
    default:
      return 'Not mapped to any framework. A gap in the mapping, not an absence of risk.';
  }
}

/** NIST IR 8477 relationships, in plain words. */
export function relationshipDescription(relationship: Relationship): string {
  switch (relationship) {
    case 'equivalent':
      return 'Equivalent: the control and this check address the same outcome.';
    case 'subset-of':
      return 'Subset of: this check addresses part of a broader control.';
    case 'superset-of':
      return 'Superset of: this check is broader than the control.';
    case 'intersects-with':
      return 'Intersects with: they overlap without either containing the other.';
    default:
      return 'Not related.';
  }
}
